<template>
    <input-group
        :model-value="selectedTimingTime"
        :schema="object().required()"
        @update:model-value="timingType = $event.value"
    >
        <template #label>
            <input-label>
                <slot name="label">
                    Wanneer wil je de flow laten starten?
                </slot>
            </input-label>
        </template>

        <template #input>
            <text-decorator size="xs">
                <radio-group-input
                    label-key-name="label"
                    value-key-name="value"
                    :items="availableTimingTypes"
                />
            </text-decorator>
        </template>
    </input-group>

    <flex-container
        v-if="showTimingConfiguration"
        no-wrap
        align-items="center"
    >
        <flex-container :size="2">
            Aantal
        </flex-container>
        <flex-container :size="2">
            <input-group
                v-model.number="timingQuantity"
                :schema="number().required().moreThan(0).positive()"
            >
                <template #input>
                    <text-input />
                </template>
            </input-group>
        </flex-container>
        <flex-container :size="8">
            <input-group
                :model-value="selectedTimingUnit"
                :schema="object().required()"
                @update:model-value="timingUnit = $event.value"
            >
                <template #input>
                    <select-input :items="availableTimingUnits" />
                </template>
            </input-group>
        </flex-container>
    </flex-container>
</template>
<script setup lang="ts">
    import {
        FlexContainer,
        InputGroup,
        InputLabel,
        RadioGroupInput,
        TextInput,
    } from '@keyvue/one-ui';

    import { number, object } from 'yup';
    import { computed, ref, watch } from 'vue';

    type TimingType = 'before' | 'on' | 'after';
    type TimingUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months';

    interface Props {
        timingType?: TimingType,
        timingQuantity?: number,
        timingUnit?: TimingUnit,
        labels?: {
            before: string,
            on: string,
            after: string,
        }
    }

    const props = withDefaults(defineProps<Props>(), {
        timingType: 'before',
        timingQuantity: 2,
        timingUnit: 'days',
        labels: () => ({
            before: 'Voor',
            on: 'Op dag',
            after: 'Na',
        }),
    });

    const emit = defineEmits<{
        (e: 'update:timingType', value: TimingType): void;
        (e: 'update:timingQuantity', value: number): void;
        (e: 'update:timingUnit', value: TimingUnit): void;
    }>();

    const timingType = ref(props.timingType);
    const timingQuantity = ref(props.timingQuantity);
    const timingUnit = ref(props.timingUnit);

    // v-model.number doesn't work with text-input, so we need to convert the value to a number
    const timingQuantityNumberValue = computed(() => Number.parseFloat(timingQuantity.value.toString()));

    const availableTimingTypes = computed<{ value: TimingType, label: string }[]>(() => ([
        {
            value: 'before',
            label: props.labels.before,
        },
        {
            value: 'on',
            label: props.labels.on,
        },
        {
            value: 'after',
            label: props.labels.after,
        },
    ]));

    const availableTimingUnits = computed<{ value: TimingUnit, label: string }[]>(() => [
        {
            value: 'minutes',
            label: timingQuantityNumberValue.value === 1 ? 'minuut' : 'minuten',
        },
        {
            value: 'hours',
            label: timingQuantityNumberValue.value === 1 ? 'uur' : 'uren',
        },
        {
            value: 'days',
            label: timingQuantityNumberValue.value === 1 ? 'dag' : 'dagen',
        },
        {
            value: 'weeks',
            label: timingQuantityNumberValue.value === 1 ? 'week' : 'weken',
        },
        {
            value: 'months',
            label: timingQuantityNumberValue.value === 1 ? 'maand' : 'maanden',
        },
    ]);

    const selectedTimingTime = computed(() => {
        return availableTimingTypes.value.find((entry) => entry.value === timingType.value);
    });

    const selectedTimingUnit = computed(() => {
        return availableTimingUnits.value.find((entry) => entry.value === timingUnit.value);
    });

    const showTimingConfiguration = computed(() => {
        return timingType.value !== 'on';
    });

    watch(() => timingType.value, () => {
        emit('update:timingType', timingType.value);
    });

    watch(() => timingQuantity.value, () => {
        emit('update:timingQuantity', timingQuantityNumberValue.value);
    });

    watch(() => timingUnit.value, () => {
        emit('update:timingUnit', timingUnit.value);
    });
</script>
